import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { onboardingApi } from '../api/onboarding.api'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { useAuthStore } from '@/store/auth.store'
import { useTranslation, Trans } from 'react-i18next'

interface OnboardingStep3Props {
  personaName?: string
}

export function OnboardingStep3({ personaName }: OnboardingStep3Props) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)

  const handleFinish = async () => {
    setIsSubmitting(true)
    setApiError(null)
    try {
      await onboardingApi.postStep3()
      navigate('/chat', { replace: true })
    } catch {
      setApiError(t('onboarding.error'))
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col items-center gap-6 py-4 text-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-mindeia-success-light">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
          <path
            d="M8 20l8 8 16-16"
            stroke="#16A34A"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-bold text-neutral-900">
          {t('onboarding.step3.title', {
            name: user?.first_name ? `, ${user.first_name}` : '',
          })}
        </h2>
        <p className="text-neutral-600 max-w-sm mx-auto">
          {personaName ? (
            <Trans
              i18nKey="onboarding.step3.intro"
              values={{ persona: personaName }}
              components={[<strong key="persona" className="text-neutral-900" />]}
            />
          ) : (
            t('onboarding.step3.intro_no_persona')
          )}
        </p>
      </div>

      <div className="w-full max-w-xs rounded-xl border border-surface-200 bg-surface-50 p-4 text-left">
        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600 mb-2">{t('onboarding.step3.summary')}</p>
        <div className="flex flex-col gap-1.5">
          <div className="flex justify-between text-sm">
            <span className="text-neutral-600">{t('onboarding.step3.account')}</span>
            <span className="font-medium text-neutral-900">{user?.email}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-neutral-600">{t('onboarding.step3.name')}</span>
            <span className="font-medium text-neutral-900">
              {user?.first_name} {user?.last_name}
            </span>
          </div>
          {personaName && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">{t('onboarding.step3.teacher')}</span>
              <span className="font-medium text-mindeia-primary">{personaName}</span>
            </div>
          )}
        </div>
      </div>

      {apiError && <Alert variant="error">{apiError}</Alert>}

      <Button onClick={handleFinish} isLoading={isSubmitting} size="lg" className="min-w-[220px]">
        {t('onboarding.step3.submit')}
      </Button>
    </div>
  )
}
